import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  ComponentParameter,
  MappingEntry,
  MappingScopeKey,
  UnmappedElement,
} from "../types";
import type { MappingLayoutMode } from "./useMappingElements";
import { mappingSelectionKey } from "./useMappingElements";
import { getElementsMissingMappings } from "../utils/mappingValidation";
import { getParamOptionValue } from "../utils/paramOptions";

/**
 * Represents the user's selection for a single (scope, column) pair.
 * - `undefined` — the user has not made a choice yet
 * - `null`      — the user explicitly chose "— skip —"
 * - `ComponentParameter` — the user picked a specific parameter
 */
type ColumnSelection = ComponentParameter | null | undefined;

/** Outer key: elementQualifiedName (grouped) or componentCode (individual). */
type SelectionMap = Record<string, Record<string, ColumnSelection>>;

const EMPTY_SELECTIONS: SelectionMap = {};

/**
 * Keep the first column that claims a parameter; clear later duplicates so
 * seeded saved mappings cannot violate one-param→one-column.
 */
function dedupeScopeSelections(
  scope: Record<string, ColumnSelection>,
): Record<string, ColumnSelection> {
  const seen = new Set<string>();
  const next: Record<string, ColumnSelection> = { ...scope };
  for (const columnName of Object.keys(next)) {
    const selection = next[columnName];
    if (selection == null) continue;
    const optionValue = getParamOptionValue(selection);
    if (seen.has(optionValue)) {
      next[columnName] = null;
    } else {
      seen.add(optionValue);
    }
  }
  return next;
}

function normalizeInitialSelections(initial: SelectionMap): SelectionMap {
  const normalized: SelectionMap = {};
  for (const [scopeKey, scope] of Object.entries(initial)) {
    normalized[scopeKey] = dedupeScopeSelections(scope);
  }
  return normalized;
}

/**
 * Manages per-scope, per-column mapping form state for the Map Columns screen.
 */
export function useMapColumns(
  elements: UnmappedElement[],
  dataColumns: string[],
  layoutMode: MappingLayoutMode,
  initialSelections: SelectionMap = EMPTY_SELECTIONS,
) {
  const [selections, setSelectionsState] = useState<SelectionMap>({});

  // Re-seed when saved mappings arrive or when the user toggles grouped/individual.
  useEffect(() => {
    setSelectionsState(normalizeInitialSelections(initialSelections));
  }, [initialSelections, layoutMode]);

  const setSelection = useCallback(
    (
      scopeKey: string,
      columnName: string,
      parameter: ComponentParameter | null,
    ) => {
      setSelectionsState((prev) => {
        const previousScope = prev[scopeKey] ?? {};
        const nextScope: Record<string, ColumnSelection> = {
          ...previousScope,
          [columnName]: parameter,
        };
        // One parameter → one column: clear the same param from other columns.
        if (parameter != null) {
          const optionValue = getParamOptionValue(parameter);
          for (const [otherColumn, selection] of Object.entries(previousScope)) {
            if (
              otherColumn !== columnName &&
              selection != null &&
              getParamOptionValue(selection) === optionValue
            ) {
              nextScope[otherColumn] = null;
            }
          }
        }
        return { ...prev, [scopeKey]: nextScope };
      });
    },
    [],
  );

  const isReady = useMemo(() => elements.length > 0, [elements]);

  const elementsMissingMappings = useMemo(
    () =>
      getElementsMissingMappings(
        elements,
        dataColumns,
        selections,
        layoutMode,
      ),
    [elements, dataColumns, selections, layoutMode],
  );

  const buildMappingEntries = useCallback((): MappingEntry[] => {
    const entries: MappingEntry[] = [];
    for (const element of elements) {
      const scopeKey = mappingSelectionKey(element, layoutMode);
      const scopeSelections = selections[scopeKey] ?? {};
      for (const col of dataColumns) {
        const param = scopeSelections[col];
        if (param != null) {
          entries.push({
            ...(layoutMode === "individual"
              ? { componentName: element.componentCode }
              : {}),
            elementQualifiedName: param.elementQualifiedName,
            parameterQualifiedName: param.parameterQualifiedName,
            parameterDisplayName: param.displayName,
            columnName: col,
          });
        }
      }
    }
    return entries;
  }, [elements, dataColumns, selections, layoutMode]);

  const buildReplacedScopes = useCallback((): MappingScopeKey[] => {
    return elements.map((element) => ({
      ...(layoutMode === "individual"
        ? { componentName: element.componentCode }
        : {}),
      elementQualifiedName: element.elementQualifiedName,
    }));
  }, [elements, layoutMode]);

  const buildMappingsSavePayload = useCallback(() => {
    return {
      entries: buildMappingEntries(),
      replaceScopes: buildReplacedScopes(),
    };
  }, [buildMappingEntries, buildReplacedScopes]);

  return {
    selections,
    setSelection,
    isReady,
    elementsMissingMappings,
    buildMappingEntries,
    buildMappingsSavePayload,
  };
}
